import { useCallback, useEffect, useRef, useState } from 'react';

import type { DeploymentDetail } from '@/api/deploy';
import { getDeploymentDetail, getDeploymentStatus, runDbScript } from '@/api/deploy';
import { ConfirmScriptExecutionModal } from '@/components/databases/confirm-script-execution-modal';
import { useDatabaseDefinitions, useDatabaseScripts, useEnvironments } from '@/hooks/use-configuration';
import { useEnvironmentStore } from '@/stores/environment-store';
import { useToastStore } from '@/stores/toast-store';

// --- Types ---

type ExecutionStatus = 'IDLE' | 'QUEUED' | 'RUNNING' | 'SUCCESS' | 'FAILED';

// --- Loading State ---

function LoadingState() {
  return (
    <div className="animate-pulse space-y-2">
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-10 rounded-lg bg-gray-200 dark:bg-gray-700" />
      ))}
    </div>
  );
}

// --- Execution Status Indicator ---

function ExecutionStatusIndicator({ status }: { status: ExecutionStatus }) {
  if (status === 'IDLE') return null;

  const statusConfig: Record<Exclude<ExecutionStatus, 'IDLE'>, { label: string; className: string; animate: boolean }> = {
    QUEUED: { label: 'Queued', className: 'text-amber-600 dark:text-amber-400', animate: true },
    RUNNING: { label: 'Running', className: 'text-blue-600 dark:text-blue-400', animate: true },
    SUCCESS: { label: 'Success', className: 'text-green-600 dark:text-green-400', animate: false },
    FAILED: { label: 'Failed', className: 'text-red-600 dark:text-red-400', animate: false },
  };

  const config = statusConfig[status];

  return (
    <div className={`flex items-center gap-2 text-sm font-medium ${config.className}`}>
      {config.animate && (
        <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
        </svg>
      )}
      {status === 'SUCCESS' && (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      )}
      {status === 'FAILED' && (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      )}
      <span>{config.label}</span>
    </div>
  );
}

// --- Update Schema Tab ---

interface UpdateSchemaTabProps {
  projectKey: string;
}

export function UpdateSchemaTab({ projectKey }: UpdateSchemaTabProps) {
  const [selectedDatabaseId, setSelectedDatabaseId] = useState<string>('');
  const [selectedScript, setSelectedScript] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Execution state
  const [executionStatus, setExecutionStatus] = useState<ExecutionStatus>('IDLE');
  const [executionDetail, setExecutionDetail] = useState<DeploymentDetail | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Stores
  const { selectedEnvironmentId } = useEnvironmentStore();
  const addToast = useToastStore((state) => state.addToast);

  // Fetch data
  const {
    data: databases = [],
    isLoading: databasesLoading,
  } = useDatabaseDefinitions(projectKey);

  const {
    data: scripts = [],
    isLoading: scriptsLoading,
    isError: scriptsError,
    refetch: refetchScripts,
  } = useDatabaseScripts(projectKey, selectedDatabaseId);

  const { data: environments = [] } = useEnvironments(projectKey);

  // Derived values
  const selectedDatabase = databases.find((db) => db.id === selectedDatabaseId);
  const selectedEnvironment = environments.find((env) => env.id === selectedEnvironmentId);
  const canExecute = !!selectedDatabaseId && !!selectedScript && !!selectedEnvironmentId;
  const isRunning = executionStatus === 'QUEUED' || executionStatus === 'RUNNING';

  // Cleanup poll timer on unmount
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
      }
    };
  }, []);

  const handleDatabaseChange = (databaseId: string) => {
    setSelectedDatabaseId(databaseId);
    setSelectedScript(null);
    // Reset execution state when switching databases
    setExecutionStatus('IDLE');
    setExecutionDetail(null);
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = undefined;
    }
  };

  // --- Polling logic ---

  const pollDeploymentStatus = useCallback((runId: string) => {
    const poll = async () => {
      try {
        const status = await getDeploymentStatus(runId);
        if (status.status === 'RUNNING') {
          setExecutionStatus('RUNNING');
          pollTimerRef.current = setTimeout(poll, 3000);
        } else if (status.status === 'SUCCESS' || status.status === 'FAILED') {
          setExecutionStatus(status.status);
          // Fetch the full detail with logs
          try {
            const detail = await getDeploymentDetail(runId);
            setExecutionDetail(detail);
          } catch {
            // Non-critical: we still show the status
          }
        } else {
          // QUEUED — keep polling
          pollTimerRef.current = setTimeout(poll, 3000);
        }
      } catch {
        // If polling fails, keep retrying
        pollTimerRef.current = setTimeout(poll, 5000);
      }
    };
    pollTimerRef.current = setTimeout(poll, 2000);
  }, []);

  // --- Execution trigger ---

  const handleConfirmExecution = useCallback(async () => {
    if (!selectedScript || !selectedEnvironmentId || !selectedDatabaseId) return;

    setShowConfirmModal(false);
    setExecutionStatus('QUEUED');
    setExecutionDetail(null);

    try {
      const response = await runDbScript({
        projectKey,
        environment: selectedEnvironmentId,
        databaseId: selectedDatabaseId,
        script: selectedScript,
      });

      addToast({
        type: 'success',
        message: `Script execution started. Run ID: ${response.runId}`,
        duration: 5000,
      });

      // Begin polling
      pollDeploymentStatus(response.runId);
    } catch (err: unknown) {
      setExecutionStatus('FAILED');
      const message =
        err instanceof Error ? err.message : 'Failed to start script execution. Please try again.';
      addToast({ type: 'error', message, duration: 8000 });
    }
  }, [selectedScript, selectedEnvironmentId, selectedDatabaseId, projectKey, addToast, pollDeploymentStatus]);

  return (
    <div className="space-y-4">
      {/* Rollback limitations notice */}
      <div
        role="status"
        className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3"
      >
        <p className="text-sm text-amber-700 dark:text-amber-300">
          Scripts are executed as-is against the target database. Prepare rollback scripts manually
          if needed. The system does not provide automatic rollback functionality.
        </p>
      </div>

      {/* Database selector */}
      <div className="rounded-lg border border-border p-4">
        <label
          htmlFor="db-selector"
          className="block text-sm font-medium text-content mb-1.5"
        >
          Database
        </label>
        {databasesLoading ? (
          <div className="h-9 rounded-md bg-gray-200 dark:bg-gray-700 animate-pulse" />
        ) : (
          <select
            id="db-selector"
            value={selectedDatabaseId}
            onChange={(e) => handleDatabaseChange(e.target.value)}
            className="block w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-content shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            <option value="">Select a database...</option>
            {databases.map((db) => (
              <option key={db.id} value={db.id}>
                {db.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Script browser */}
      {selectedDatabaseId && (
        <div className="rounded-lg border border-border p-4">
          <h4 className="text-sm font-medium text-content mb-3">Available Scripts</h4>

          {/* Loading state */}
          {scriptsLoading && <LoadingState />}

          {/* Error state */}
          {scriptsError && (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20"
            >
              <p className="text-sm text-red-700 dark:text-red-300">
                Failed to load scripts for this database.
              </p>
              <button
                onClick={() => refetchScripts()}
                className="mt-2 rounded-md bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
              >
                Retry
              </button>
            </div>
          )}

          {/* Empty state */}
          {!scriptsLoading && !scriptsError && scripts.length === 0 && (
            <div className="rounded-md border border-border-subtle bg-surface-raised p-4 text-center">
              <p className="text-sm text-content-tertiary">
                No SQL scripts found at the configured path
              </p>
            </div>
          )}

          {/* Script list */}
          {!scriptsLoading && !scriptsError && scripts.length > 0 && (
            <ul className="divide-y divide-border rounded-md border border-border overflow-hidden">
              {scripts.map((script) => (
                <li key={script}>
                  <button
                    type="button"
                    onClick={() => setSelectedScript(script)}
                    className={[
                      'w-full px-3 py-2.5 text-left text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-500',
                      selectedScript === script
                        ? 'bg-brand-50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-300 font-medium'
                        : 'text-content hover:bg-surface-raised',
                    ].join(' ')}
                    aria-selected={selectedScript === script}
                  >
                    {script}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Run Script button */}
      {canExecute && (
        <div className="flex items-center gap-4">
          <button
            type="button"
            disabled={isRunning}
            onClick={() => setShowConfirmModal(true)}
            className="inline-flex items-center gap-2 rounded-md bg-amber-500 px-4 py-2 text-sm font-medium text-white hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 transition-colors"
          >
            {isRunning && (
              <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            )}
            {isRunning ? 'Execution in progress...' : 'Run Script'}
          </button>
          <ExecutionStatusIndicator status={executionStatus} />
        </div>
      )}

      {/* Execution output log */}
      {executionDetail && (executionStatus === 'SUCCESS' || executionStatus === 'FAILED') && (
        <div className="rounded-lg border border-border p-4">
          <h4 className="text-sm font-medium text-content mb-2">Execution Output</h4>
          <pre
            className={[
              'overflow-auto rounded-md border p-3 text-xs font-mono whitespace-pre-wrap',
              executionStatus === 'FAILED'
                ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/10 text-red-800 dark:text-red-200'
                : 'border-border bg-surface-raised text-content',
            ].join(' ')}
            style={{ maxHeight: '500px' }}
          >
            {executionDetail.log || 'No output available.'}
          </pre>
        </div>
      )}

      {/* Confirmation modal */}
      <ConfirmScriptExecutionModal
        isOpen={showConfirmModal}
        databaseName={selectedDatabase?.name ?? ''}
        environmentName={selectedEnvironment?.name ?? ''}
        scriptFilename={selectedScript ?? ''}
        onConfirm={handleConfirmExecution}
        onCancel={() => setShowConfirmModal(false)}
      />
    </div>
  );
}
